import React, { useEffect, useState } from 'react';
import { useAppContext } from 'src/components/app-context';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';
import { Direction } from '@prisjakt/ui';
import {
  TitleSmallText,
  FontEmphasis,
  Behavior,
} from '@prisjakt/ui/text/variants';
import type { Option, OptionTypeEnum } from 'src/components/filter-dropdown';
import { FilterDropdown as SortingDropDown } from 'src/components/filter-dropdown';
import { IncludeShippingFilter } from '../offer-list-filters/components/include-shipping-filter';
import { getSortOptions } from '../utils';
import { OfferListFilters } from '../offer-list-filters';
import { ActiveFiltersTicks } from '../offer-list-filters/components/active-filters-ticks';
import type { IFilter } from 'src/components/price-list/types';
import { FilterUIPlacementEnum } from 'src/components/price-list/types';
import type { ProductMainOffer } from 'src/types';
import { MediumUp } from 'src/ui/block';
import { Header, HeaderButtons, HeaderFlex } from '../styles';
import { getApplicableFilters } from '../../../utils/price-list/filter-filters';

type PriceListHeaderProps = {
  onSort: (option: Option) => void;
  sorting: OptionTypeEnum;
  allOffers: ProductMainOffer[];
  allOffersWithActiveAdditives: ProductMainOffer[];
};

const PriceListHeader = ({
  allOffers,
  allOffersWithActiveAdditives,
  onSort,
  sorting,
}: PriceListHeaderProps) => {
  const { t } = useAppContext();
  const { getFilterByPlacement } = useOfferListFilterContext();
  const [applicableFilters, setApplicableFilters] = useState<IFilter[]>([]);
  const filtersByPlacement = getFilterByPlacement(
    FilterUIPlacementEnum.FILTER_MENU
  );

  const sortOptions = getSortOptions(t, allOffers);

  const currentSorting: Option | undefined =
    sortOptions.length === 0
      ? undefined
      : sortOptions.find(sort => sort.type === sorting) || sortOptions[0];

  useEffect(() => {
    const initializeFilters = () => {
      const updatedFilters = getApplicableFilters(
        filtersByPlacement,
        allOffers,
        allOffersWithActiveAdditives
      );
      setApplicableFilters(updatedFilters);
    };
    initializeFilters();
  }, [allOffers, allOffersWithActiveAdditives]);

  const activeFiltersInFilterMenu =
    filtersByPlacement?.filter(filter => {
      const isNotHidden = !!filter.hidden;
      const activeAdditive = !filter.active && filter.additive;
      const active = filter.active && !filter.additive;

      return isNotHidden && (active || activeAdditive);
    }) || [];

  const getSortFilter = () => {
    if (currentSorting) {
      return (
        <SortingDropDown
          alignment={Direction.Right}
          defaultSelected={currentSorting}
          options={sortOptions}
          onFilterChange={option => onSort(option)}
        />
      );
    }
    return (
      <TitleSmallText
        flex={false}
        emphasis={FontEmphasis.LOW}
        behavior={Behavior.Oneline}
      >
        {t('product', 'sorting.sorted-by-cheapest-price')}
      </TitleSmallText>
    );
  };

  return (
    <Header data-test="PricePanelHeader">
      <HeaderFlex>
        <HeaderButtons>
          <OfferListFilters
            applicableFilters={applicableFilters}
            activeOfferlistFilters={activeFiltersInFilterMenu}
            onSort={onSort}
            currentSorting={currentSorting}
            sortOptions={sortOptions}
          />
          <IncludeShippingFilter />
        </HeaderButtons>
        <MediumUp>{getSortFilter()}</MediumUp>
      </HeaderFlex>
      <ActiveFiltersTicks activeFilters={activeFiltersInFilterMenu} />
    </Header>
  );
};

export default PriceListHeader;
